import React, { useRef, useState, useCallback } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { DocumentArrowUpIcon } from "@heroicons/react/24/solid"
import { twMerge } from "tailwind-merge"

export default function DropZone({ onDrop, maxFiles = 1, accept, onRender }) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)
  const dragCounter = useRef(0)

  // Extract file extensions from accept object
  const getSupportedExtensions = () => {
    if (!accept) return []
    // Handle object format: { 'application/pdf': ['.pdf'], ... }
    if (typeof accept === "object" && !Array.isArray(accept)) {
      return Object.values(accept)
        .flat()
        .map((ext) => ext.replace(".", "").toUpperCase())
    }
    // Handle array format: ['.pdf', '.doc']
    if (Array.isArray(accept)) {
      return accept.map((ext) => ext.replace(".", "").toUpperCase())
    }
    // Handle string format: '.pdf,.doc'
    if (typeof accept === "string") {
      return accept
        .split(",")
        .map((ext) => ext.trim().replace(".", "").toUpperCase())
    }
    return []
  }

  // Validate if file type is acceptable
  const isFileAccepted = (file) => {
    if (!accept) return true

    // Convert accept object to array of mime types and extensions
    const acceptedTypes = []
    const acceptedExtensions = []

    if (typeof accept === "object" && !Array.isArray(accept)) {
      Object.entries(accept).forEach(([mimeType, extensions]) => {
        acceptedTypes.push(mimeType)
        if (Array.isArray(extensions)) {
          extensions.forEach((ext) =>
            acceptedExtensions.push(ext.toLowerCase())
          )
        }
      })
    }

    // Check if file type matches any accepted mime type
    const fileType = file.type
    const fileName = file.name
    const fileExtension = "." + fileName.split(".").pop().toLowerCase()

    // Check for wildcard mime types like 'image/*'
    const isTypeAccepted = acceptedTypes.some((type) => {
      if (type.endsWith("/*")) {
        const category = type.split("/")[0]
        return fileType.startsWith(category + "/")
      }
      return type === fileType
    })

    // Check file extension
    const isExtensionAccepted = acceptedExtensions.includes(fileExtension)

    return isTypeAccepted || isExtensionAccepted
  }

  // Handle the file input change event
  const handleInputChange = (event) => {
    event.preventDefault()
    event.stopPropagation()

    const files = event.target.files
    if (!files || files.length === 0) return

    const acceptedFiles = []
    const rejectedFiles = []

    Array.from(files)
      .slice(0, maxFiles)
      .forEach((file) => {
        if (isFileAccepted(file)) {
          acceptedFiles.push(file)
        } else {
          rejectedFiles.push(file)
        }
      })

    if (onDrop && typeof onDrop === "function") {
      onDrop(acceptedFiles, rejectedFiles)
    }

    // Reset the file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  // Handle the drop event
  const handleDrop = useCallback(
    (event) => {
      event.preventDefault()
      event.stopPropagation()

      setIsDragging(false)
      dragCounter.current = 0

      const files = event.dataTransfer.files
      if (!files || files.length === 0) return

      const acceptedFiles = []
      const rejectedFiles = []

      Array.from(files)
        .slice(0, maxFiles)
        .forEach((file) => {
          if (isFileAccepted(file)) {
            acceptedFiles.push(file)
          } else {
            rejectedFiles.push(file)
          }
        })

      if (onDrop && typeof onDrop === "function") {
        onDrop(acceptedFiles, rejectedFiles)
      }
    },
    [onDrop, maxFiles]
  )

  // Handle drag over to prevent browser default behavior
  const handleDragOver = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  // Keep track of drag enter/leave events to handle nested elements
  const handleDragEnter = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()

    dragCounter.current++
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()

    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }, [])

  // Handle click on the dropzone area
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const supportedExtensions = getSupportedExtensions()

  // Create accept attribute for file input
  const getAcceptString = () => {
    if (!accept) return undefined

    if (typeof accept === "string") {
      return accept
    }

    if (typeof accept === "object" && !Array.isArray(accept)) {
      // Create a string like "image/*,.jpg,.jpeg,.png,application/pdf,.pdf"
      const acceptValues = []

      Object.entries(accept).forEach(([mimeType, extensions]) => {
        acceptValues.push(mimeType)
        if (Array.isArray(extensions)) {
          extensions.forEach((ext) => acceptValues.push(ext))
        }
      })

      return acceptValues.join(",")
    }

    return undefined
  }

  // Create a props object to pass to the custom render function
  const renderProps = {
    isDragging,
    handleClick,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    fileInputRef,
    supportedExtensions,
    getAcceptString,
    maxFiles,
    accept,
  }

  // Always include the hidden file input
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      onChange={handleInputChange}
      accept={getAcceptString()}
      multiple={maxFiles > 1}
      style={{ display: "none" }}
    />
  )

  // Use custom render function if provided
  if (onRender && typeof onRender === "function") {
    return (
      <div>
        {fileInput}
        {onRender(renderProps)}
      </div>
    )
  }

  // Otherwise use default UI
  return (
    <div>
      <div
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={twMerge(
          "flex flex-col w-[280px] sm:w-[550px] md:w-[900px] h-80 justify-center items-center bg-paper border border-bordered rounded-xl hover:border-text hover:bg-opacity-75 hover:cursor-pointer gap-[12px] p-3 mb-7",
          isDragging ? "bg-opacity-75 border-text" : ""
        )}
      >
        {fileInput}
        <DocumentArrowUpIcon className="h-16 w-16 text-text_secondary" />
        
        <Button
          variant="admin_primary"
          className="text-link border-primary hover:bg-primary hover:text-white"
          onClick={(e) => {
            e.stopPropagation()
            handleClick()
          }}
        >
          {`Choose files`}
        </Button>

        <Typography className="font-normal text-text_secondary self-center">
          {`or drop files here`}
        </Typography>
      </div>
    </div>
  )
}
DropZone.displayName = "DropZone"